/**
 * Step 5 — User Permissions [TYPE B — Optional]
 *
 * Shows an empty state by default. User can click "Set Up Now" to reveal
 * the full User Permissions settings page embedded inline.
 * Back and Skip are both visible.
 */

import { useState } from 'react';
import { Users, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { StepWrapper } from '../StepWrapper';
import SettingsUserPermissionsPage from '@/pages/settings-user-permissions';

interface StepProps {
  onNext: () => void;
  onBack: () => void;
  onSkip: () => void;
  isLastStep: boolean;
}

export function Step5UserPermissions({ onNext, onBack, onSkip, isLastStep }: StepProps) {
  const [showSettings, setShowSettings] = useState(false);

  return (
    <StepWrapper
      barState="default"
      barMessage="Optional — you can assign user permissions later from Settings → User Permissions."
      showBack={true}
      showSkip={true}
      nextLabel={isLastStep ? 'Finish Setup →' : 'Next Step →'}
      onNext={onNext}
      onBack={onBack}
      onSkip={onSkip}
    >
      {!showSettings ? (
        /* Empty state */
        <div className="flex flex-col items-center justify-center py-20 space-y-5 text-center px-8">
          <div className="h-16 w-16 rounded-2xl bg-primary/10 flex items-center justify-center">
            <Users className="h-8 w-8 text-primary" />
          </div>
          <div className="space-y-2 max-w-md">
            <h3 className="text-lg font-semibold text-foreground">User Permissions</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Assign platform roles and access levels to your team members. This
              controls what each user can see and do in Projects. You can
              set this up now or return to it later from{' '}
              <span className="font-medium">Settings → User Permissions</span>.
            </p>
          </div>
          <Button onClick={() => setShowSettings(true)} className="gap-2">
            Set Up User Permissions Now
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      ) : (
        /* Embedded settings page */
        <div className="rounded-lg border border-border overflow-hidden bg-background shadow-sm">
          <SettingsUserPermissionsPage />
        </div>
      )}
    </StepWrapper>
  );
}
